/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useState } from 'react';
import { Space } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { TablePaginationConfig, TableProps } from 'antd';
import { IOrder, ISearchOrderReq, ISearchOrderRes } from '@/interfaces/order';
import { IDataModal } from '@/interfaces/dataModal';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate } from '@/utils/dataUtils';
import { getOrderStatusColor, getOrderStatusName } from '@/helpers/orderHelper';
import { twMerge } from 'tailwind-merge';
import MediaViewModal from '@/components/media/media-view-modal';

interface IProps {
  loading: boolean;
  criteria: ISearchOrderReq;
  order: ISearchOrderRes;
  onEditData: (item: IOrder) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => void;
}

const _orderModal: IDataModal<IOrder> = {
  open: false,
};

const CustomerOrderTable = ({
  loading,
  criteria,
  order,
  onTableChange,
}: IProps) => {
  const navigate = useNavigate();
  const [fileModal, setFileModal] = useState<IDataModal<IOrder>>(_orderModal);

  const columns: TableProps<IOrder>['columns'] = [
    {
      sorter: true,
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-blue-700',
      width: 120,
      align: 'center',
      fixed: 'left',
      render: (_, rec) => (
        <Space
          onClick={() => navigate(`/order/${rec.id}`)}
          className="text-blue-700 underline cursor-pointer"
        >
          {_}
        </Space>
      ),
    },
    {
      sorter: true,
      title: 'วันที่',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 100,
      align: 'center',
      className: 'font-medium text-gray-500',
      render: _ => formatDate(_),
    },
    // {
    //   title: 'ลูกค้า',
    //   dataIndex: 'customerName',
    //   key: 'customerName',
    //   className: 'font-bold',
    // },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      className: 'font-bold',
      align: 'center',
      width: 120,
    },
    {
      title: 'SO No.',
      dataIndex: 'soNo',
      key: 'soNo',
      className: 'font-bold',
      align: 'center',
      width: 120,
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      className: 'font-bold',
      align: 'center',
      width: 130,
    },
    // {
    //   title: 'QT No.',
    //   dataIndex: 'qtNo',
    //   key: 'qtNo',
    //   className: 'font-bold',
    //   align: 'center',
    //   width: 120,
    // },
    {
      title: 'ขนส่ง',
      dataIndex: 'transportation',
      key: 'transportation',
      width: 100,
    },
    {
      title: 'ที่จัดส่ง',
      dataIndex: 'shippingAddress',
      key: 'shippingAddress',
      render: (_, rec) => <div>{rec?.shippingAddress?.name}</div>,
    },
    {
      title: 'สินค้า',
      dataIndex: 'orderItems',
      key: 'orderItems',
      render: (_, rec) => (
        <div className="truncate overflow-hidden">
          {rec?.orderItems
            ?.map(i => {
              return `${i.productName}`;
            })
            .join(', ')}
        </div>
      ),
    },
    {
      sorter: true,
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 100,
      render: (_, rec) => {
        const name = getOrderStatusName(_, rec.transportation);
        const status = getOrderStatusColor(_, rec.transportation);
        return (
          <div className={twMerge(status?.bg, status?.text, 'rounded py-0.5')}>
            {name}
          </div>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      render: (_, rec) => (
        <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
          <FileSearchOutlined
            className="text-blue-700 px-1.5"
            onClick={() =>
              setFileModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IOrder>
        columns={columns}
        criteria={criteria}
        data={order}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1600}
        scroll
      />
      <Suspense>
        <MediaViewModal
          modelId={Number(fileModal?.id) || null}
          modelType={'order'}
          open={fileModal?.open}
          onCancel={function (): void {
            setFileModal({
              ..._orderModal,
              open: false,
            });
          }}
        />
      </Suspense>
    </div>
  );
};

export default CustomerOrderTable;
